import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, BookOpen, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const LoanLedgerReport = () => {
  const { isAdmin } = useAuth();
  const [loans, setLoans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedLoanId, setSelectedLoanId] = useState('');
  const [loanLedgerData, setLoanLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchLoans(); }, [selectedBranch]);

  const fetchLoans = async () => {
    try {
      const branchParam = selectedBranch !== 'all' ? { branch_id: selectedBranch } : {};
      const [loanResp, branchResp] = await Promise.all([api.get('/loans', { params: branchParam }), api.get('/branches')]);
      setLoans(loanResp.data);
      setBranches(branchResp.data);
    } catch { toast.error('Failed to fetch loans'); }
  };

  const fetchLoanLedger = async (loanId) => {
    if (!loanId) return;
    setLoading(true);
    try { const resp = await api.get(`/reports/loan-ledger/${loanId}`); setLoanLedgerData(resp.data); }
    catch { toast.error('Failed to fetch loan ledger'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!loanLedgerData) return;
    const d = loanLedgerData;
    const rows = (d.ledger_entries || []).map(e => ({
      'Sl.No': e.sl_no,
      'Date': e.date ? new Date(e.date).toLocaleDateString('en-IN') : '',
      'Collection Amount': Math.round(e.collection_amount || 0),
      'Principal Repaid': Math.round(e.principal_repaid || 0),
      'Interest Repaid': Math.round(e.interest_repaid || 0),
      'Penalty Repaid': Math.round(e.penalty_repaid || 0),
      'Principal Outstanding': Math.round(e.principal_outstanding || 0),
      'Interest Outstanding': Math.round(e.interest_outstanding || 0),
      'Total Balance': Math.round(e.total_balance || 0),
    }));
    exportToExcel({
      filename: `WWAGS_Loan_Ledger_${d.loan?.loan_id || 'loan'}`,
      sheetName: `Ledger ${d.loan?.loan_id || ''}`.slice(0, 31),
      rows,
      kpis: [
        { label: 'Loan ID', value: d.loan?.loan_id || '' },
        { label: 'Entrepreneur', value: d.loan?.entrepreneur_name || '' },
        { label: 'Loan Amount', value: Math.round(d.summary?.loan_amount || 0) },
        { label: 'Interest Rate', value: `${d.loan?.interest_rate || 0}%` },
        { label: 'Tenure', value: `${d.loan?.tenure_months || 0} months` },
        { label: 'Principal Repaid', value: Math.round(d.summary?.total_principal_repaid || 0) },
        { label: 'Interest Repaid', value: Math.round(d.summary?.total_interest_repaid || 0) },
        { label: 'Total Balance', value: Math.round(d.summary?.total_balance || 0) },
      ],
    });
  };

  const handlePrint = () => {
    if (!loanLedgerData) return;
    const d = loanLedgerData;
    const rows = (d.ledger_entries||[]).map((s, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td style="text-align:center">${s.sl_no}</td><td>${s.date?new Date(s.date).toLocaleDateString('en-IN'):'-'}</td><td style="text-align:right">Rs.${(s.collection_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.principal_repaid||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.interest_repaid||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.penalty_repaid||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.principal_outstanding||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.interest_outstanding||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(s.total_balance||0).toLocaleString('en-IN')}</td></tr>`).join('');
    const body = `<div class="cards"><div class="card"><small>Loan ID</small><b>${d.loan.loan_id||'-'}</b></div><div class="card"><small>Entrepreneur</small><b>${d.loan.entrepreneur_name||'-'}</b></div><div class="card"><small>Loan Amount</small><b>Rs.${(d.summary.loan_amount||0).toLocaleString('en-IN')}</b></div><div class="card"><small>Balance</small><b>Rs.${(d.summary.total_balance||0).toLocaleString('en-IN')}</b></div></div><table><thead><tr><th>Sl.No</th><th>Date</th><th>Amount</th><th>Principal</th><th>Interest</th><th>Penalty</th><th>Principal O/S</th><th>Interest O/S</th><th>Balance</th></tr></thead><tbody>${rows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('Loan Ledger Report', body));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="loan-ledger-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1"><h1 className="text-2xl font-bold text-slate-900">Loan Ledger</h1><p className="text-sm text-slate-600">Date-wise ledger with principal & interest breakdown</p></div>
        {loanLedgerData && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {loanLedgerData && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        {isAdmin() && (
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={selectedLoanId} onValueChange={(v) => { setSelectedLoanId(v); fetchLoanLedger(v); }}>
          <SelectTrigger className="w-80" data-testid="select-loan-for-ledger"><SelectValue placeholder="Select a loan to view ledger" /></SelectTrigger>
          <SelectContent>{loans.map(l => <SelectItem key={l.loan_id} value={l.loan_id}>{l.loan_id} - {l.entrepreneur_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>}

      {loanLedgerData && !loading && (
        <>
          <div className="bg-gradient-to-r from-slate-50 to-emerald-50 rounded-lg border border-slate-200 p-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div><p className="text-xs text-slate-500 font-medium">Loan ID</p><p className="text-sm font-bold text-slate-900">{loanLedgerData.loan.loan_id}</p></div>
              <div className="md:col-span-2"><p className="text-xs text-slate-500 font-medium">Entrepreneur</p><p className="text-sm font-bold text-slate-900">{loanLedgerData.loan.entrepreneur_name}</p></div>
              <div><p className="text-xs text-slate-500 font-medium">Loan Amount</p><p className="text-sm font-bold text-emerald-700">Rs.{loanLedgerData.summary.loan_amount?.toLocaleString('en-IN') || 0}</p></div>
              <div><p className="text-xs text-slate-500 font-medium">Interest Rate</p><p className="text-sm font-bold text-slate-900">{loanLedgerData.loan.interest_rate}% p.a.</p></div>
              <div><p className="text-xs text-slate-500 font-medium">Tenure</p><p className="text-sm font-bold text-slate-900">{loanLedgerData.loan.tenure_months} months</p></div>
              <div><p className="text-xs text-slate-500 font-medium">EMI</p><p className="text-sm font-bold text-slate-900">Rs.{loanLedgerData.loan.emi_amount?.toFixed(2) || 0}</p></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4"><p className="text-xs text-emerald-700 mb-1">Principal Repaid</p><p className="text-lg font-bold text-emerald-900">Rs.{loanLedgerData.summary.total_principal_repaid?.toLocaleString('en-IN') || 0}</p></div>
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4"><p className="text-xs text-blue-700 mb-1">Interest Repaid</p><p className="text-lg font-bold text-blue-900">Rs.{loanLedgerData.summary.total_interest_repaid?.toLocaleString('en-IN') || 0}</p></div>
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4"><p className="text-xs text-amber-700 mb-1">Penalty Repaid</p><p className="text-lg font-bold text-amber-900">Rs.{loanLedgerData.summary.total_penalty_repaid?.toLocaleString('en-IN') || 0}</p></div>
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-4"><p className="text-xs text-orange-700 mb-1">Principal O/S</p><p className="text-lg font-bold text-orange-900">Rs.{loanLedgerData.summary.principal_outstanding?.toLocaleString('en-IN') || 0}</p></div>
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-4"><p className="text-xs text-purple-700 mb-1">Interest O/S</p><p className="text-lg font-bold text-purple-900">Rs.{loanLedgerData.summary.interest_outstanding?.toLocaleString('en-IN') || 0}</p></div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-4"><p className="text-xs text-red-700 mb-1">Total Balance</p><p className="text-lg font-bold text-red-900">Rs.{loanLedgerData.summary.total_balance?.toLocaleString('en-IN') || 0}</p></div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
            <table className="w-full">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-semibold">Sl.No</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold">Date</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Amount</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Principal</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Interest</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Penalty</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Principal O/S</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Interest O/S</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loanLedgerData.ledger_entries.length === 0 ? (
                  <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-500">No collections recorded</td></tr>
                ) : loanLedgerData.ledger_entries.map((entry, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-3 py-3 text-sm text-center font-medium">{entry.sl_no}</td>
                    <td className="px-3 py-3 text-sm">{entry.date ? new Date(entry.date).toLocaleDateString('en-IN') : '-'}</td>
                    <td className="px-3 py-3 text-sm text-right font-medium text-emerald-700">Rs.{entry.collection_amount?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right">Rs.{entry.principal_repaid?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right">Rs.{entry.interest_repaid?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right text-amber-600">Rs.{entry.penalty_repaid?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right text-orange-600">Rs.{entry.principal_outstanding?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right text-purple-600">Rs.{entry.interest_outstanding?.toLocaleString('en-IN') || 0}</td>
                    <td className="px-3 py-3 text-sm text-right font-semibold text-red-600">Rs.{entry.total_balance?.toLocaleString('en-IN') || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedLoanId && !loading && (
        <div className="text-center py-12 text-slate-500"><BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Select a loan above to view its detailed ledger</p></div>
      )}
    </div>
  );
};

export default LoanLedgerReport;
